import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useChatStore } from "../store/useChatStore";

import MessengerSidebar from "../components/MessengerSidebar";
import ChatContainer from "../components/ChatContainer";

import sdcNotificationLogo from "../assets/sdc-notification.png";
import { getSocket } from "../socket";
import "./HomePage.css";

const getNotificationPreview = (message = {}) => {
  const text =
    message?.content ||
    message?.text ||
    message?.message ||
    message?.caption ||
    "";

  const cleanText = String(text || "").trim();

  if (cleanText) {
    return cleanText;
  }

  if (message?.fileName) {
    return `📎 ${message.fileName}`;
  }

  if (Array.isArray(message?.files) && message.files.length > 0) {
    const firstFile = message.files[0];

    const fileName =
      firstFile?.fileName ||
      firstFile?.name ||
      firstFile?.originalName ||
      firstFile?.filename ||
      "";

    return fileName ? `📎 ${fileName}` : "📎 Document received";
  }

  if (message?.file && typeof message.file === "object") {
    const fileName =
      message.file?.fileName ||
      message.file?.name ||
      message.file?.originalName ||
      message.file?.filename ||
      "";

    return fileName ? `📎 ${fileName}` : "📎 Document received";
  }

  if (message?.file) {
    return "📎 Document received";
  }

  return "New message received";
};

const HomePage = () => {
  const { selectedUser } = useChatStore();

  const [isCallModalOpen, setIsCallModalOpen] = useState(false);

  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );

  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMobile = vw <= 768;

  const socket = getSocket();
  const pageRef = useRef(null);

  /* ======================================================
     FIT PAGE EXACTLY INTO THE VISIBLE SCREEN
     (measures real top position, no fixed navbar guess,
      and resets any outer scroll so the chat header is visible)
  ====================================================== */

  useLayoutEffect(() => {
    const resetOuterScroll = () => {
      window.scrollTo(0, 0);
      let p = pageRef.current?.parentElement;
      while (p) {
        if (p.scrollTop) p.scrollTop = 0;
        p = p.parentElement;
      }
    };

    const fit = () => {
      const el = pageRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(320, window.innerHeight - top)}px`;
    };

    const run = () => {
      fit();
      resetOuterScroll();
    };

    run();
    // layout (navbar, fonts) may settle a moment later
    const t1 = setTimeout(run, 100);
    const t2 = setTimeout(run, 400);

    window.addEventListener("resize", run);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(fit);
      ro.observe(document.body);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", run);
      ro?.disconnect();
    };
  }, []);

  /* ======================================================
     LOCK OUTER PAGE SCROLL
  ====================================================== */

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev || "auto";
    };
  }, []);

  /* ======================================================
     RESIZE
  ====================================================== */

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  /* ======================================================
     CLOSE DRAWER ON DESKTOP
  ====================================================== */

  useEffect(() => {
    if (!isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  /* ======================================================
     ESC CLOSE DRAWER
  ====================================================== */

  useEffect(() => {
    if (!isMobile || !drawerOpen) {
      return;
    }

    const onKey = (e) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [isMobile, drawerOpen]);

  /* ======================================================
     NOTIFICATION PERMISSION
  ====================================================== */

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  /* ======================================================
     NEW MESSAGE NOTIFICATION
  ====================================================== */

  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (message) => {
      console.log("📩 HOME PAGE MESSAGE:", message);

      if (
        !("Notification" in window) ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      const senderName =
        message?.senderName || message?.senderId?.name || "User";

      const preview = getNotificationPreview(message);

      console.log("🔔 Notification Preview:", preview);

      const notification = new Notification(`💬 ${senderName}`, {
        body: preview,
        icon: sdcNotificationLogo,
        badge: sdcNotificationLogo,
        tag: message?._id ? `message-${message._id}` : `message-${Date.now()}`,
        requireInteraction: false,
      });

      notification.onclick = () => {
        try {
          window.focus();
        } catch {}

        try {
          window.location.assign("/messenger");
        } catch {}

        try {
          notification.close();
        } catch {}
      };
    };

    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
    };
  }, [socket]);

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="hp" ref={pageRef}>
      <div className="hp__body">
        {isMobile && !drawerOpen && (
          <button className="hp__menu-btn" onClick={() => setDrawerOpen(true)}>
            ☰
          </button>
        )}

        {isMobile && (
          <div
            className={`hp__backdrop ${drawerOpen ? "is-open" : ""}`}
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {isMobile ? (
          <div className={`hp__drawer ${drawerOpen ? "is-open" : ""}`}>
            <div className="hp__drawer-inner">
              <MessengerSidebar
                forceOpen={drawerOpen}
                onSelect={() => setDrawerOpen(false)}
                onBack={() => setDrawerOpen(false)}
              />
            </div>
          </div>
        ) : (
          <div className="hp__sidebar">
            <MessengerSidebar forceOpen={true} />
          </div>
        )}

        <div className="hp__chat">
          <ChatContainer />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
